import { Minus, Plus } from "lucide-react";
import { inr, fmtDateTime } from "@/lib/format";
import { useSite } from "@/context/SiteContext";

export const PriceBlock = ({ product, size = "lg" }) => (
  <div data-testid="price-block">
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {product.on_sale && (
        <span data-testid="sale-badge" className="rounded-full bg-red-700 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-white">Sale</span>
      )}
      <span data-testid="product-price" className={`font-heading font-extrabold text-brand-ink ${size === "lg" ? "text-3xl md:text-4xl" : "text-2xl"}`}>
        {inr(product.price)}
      </span>
      <span className="text-base text-slate-600">
        MRP {product.mrp > product.price ? <s data-testid="product-mrp">{inr(product.mrp)}</s> : <span data-testid="product-mrp">{inr(product.mrp)}</span>}
      </span>
    </div>
    {product.saving > 0 && (
      <p data-testid="product-saving" className="mt-1 text-sm font-semibold text-brand-greenDark">
        You save {inr(product.saving)} ({product.saving_percent}% off MRP)
      </p>
    )}
    <p className="mt-0.5 text-sm text-slate-600">Inclusive of all taxes</p>
    {product.on_sale && product.sale_ends_at && (
      <p className="mt-0.5 text-sm font-medium text-red-800" data-testid="sale-ends">Sale ends {fmtDateTime(product.sale_ends_at)}</p>
    )}
  </div>
);

export const StockBadge = ({ product }) => {
  const { settings } = useSite();
  if (!settings.online_payments_enabled) return null;
  return product.in_stock ? (
    <span data-testid="stock-status" className="inline-flex items-center gap-2 rounded-full bg-brand-greenTint px-3 py-1 text-sm font-semibold text-brand-greenDeep">
      <span className="h-2 w-2 rounded-full bg-brand-green" aria-hidden="true" />
      {product.stock <= 10 ? `In stock – only ${product.stock} left` : "In stock"}
    </span>
  ) : (
    <span data-testid="stock-status" className="inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-sm font-bold text-red-800 ring-1 ring-red-200">
      Out of Stock
    </span>
  );
};

export const QtySelector = ({ value, onChange, max, min = 1, testid = "qty" }) => (
  <div className="inline-flex items-center rounded-full border border-slate-300 bg-white" role="group" aria-label="Quantity">
    <button type="button" aria-label="Decrease quantity" data-testid={`${testid}-decrease`} disabled={value <= min} onClick={() => onChange(value - 1)} className="flex h-10 w-10 items-center justify-center rounded-full text-brand-ink hover:bg-brand-tint disabled:opacity-40">
      <Minus className="h-4 w-4" aria-hidden="true" />
    </button>
    <span data-testid={`${testid}-value`} className="w-9 text-center text-base font-semibold" aria-live="polite">{value}</span>
    <button type="button" aria-label="Increase quantity" data-testid={`${testid}-increase`} disabled={value >= max} onClick={() => onChange(value + 1)} className="flex h-10 w-10 items-center justify-center rounded-full text-brand-ink hover:bg-brand-tint disabled:opacity-40">
      <Plus className="h-4 w-4" aria-hidden="true" />
    </button>
  </div>
);
